/*
  Blocks the checkout button of app-rendered cart drawers for visitors who are
  not wholesale customers and have wholesale-only products in their cart.

  The theme's own cart and side cart are guarded in Liquid. A cart drawer
  rendered by an app (Zipify OneClickUpsell) is built in the browser from the
  cart data, so Liquid cannot change it. This script asks the theme whether the
  current cart is restricted, using the same rule as the Liquid guard
  (templates/cart.wholesale-guard.liquid), then disables the drawer's checkout
  button and shows the merchant-editable notice.

  It is only loaded for visitors without the wholesale-access tag. To guard
  another app's checkout button, add its selector to checkoutSelectors.
*/
class WholesaleCartGuard {
    constructor (scriptElement) {
        this.message = scriptElement.dataset.message || '';
        this.loginUrl = scriptElement.dataset.loginUrl || '';
        this.loginLabel = scriptElement.dataset.loginLabel || '';
        this.checkoutSelectors = ['.ocu-checkout-button'];
        this.drawerSelector = '.ocu-cart-drawer';
        this.noticeClass = 'wholesale-cart-guard-notice';
        this.restrictedTitles = '';
        this.refreshTimer = null;
        this.guardUrl = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart?view=wholesale-guard';
    }

    init () {
        document.addEventListener('click', this.blockCheckoutClick.bind(this), true);

        const observer = new MutationObserver((mutations) => {
            const hasDrawerChange = mutations.some((mutation) => this.isDrawerMutation(mutation));

            if (!hasDrawerChange) {
                return;
            }

            // Re-apply the last known state at once so a re-rendered button is never clickable, then confirm it.
            this.applyState();
            this.scheduleRefresh();
        });

        observer.observe(document.body, { childList: true, subtree: true });

        if (document.querySelector(this.drawerSelector)) {
            this.scheduleRefresh();
        }
    }

    isDrawerMutation (mutation) {
        const isOwnNotice = Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some((node) => {
            return node.nodeType === 1 && node.classList.contains(this.noticeClass);
        });

        if (isOwnNotice) {
            return false;
        }

        const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;

        if (target && target.closest(this.drawerSelector)) {
            return true;
        }

        return Array.from(mutation.addedNodes).some((node) => {
            return node.nodeType === 1 && (node.matches(this.drawerSelector) || node.querySelector(this.drawerSelector));
        });
    }

    scheduleRefresh () {
        window.clearTimeout(this.refreshTimer);
        this.refreshTimer = window.setTimeout(this.refreshState.bind(this), 400);
    }

    async refreshState () {
        try {
            const response = await fetch(this.guardUrl, { credentials: 'same-origin' });
            this.restrictedTitles = response.ok ? (await response.text()).trim() : '';
        } catch (error) {
            this.restrictedTitles = '';
        }

        this.applyState();
    }

    getCheckoutButtons () {
        return Array.from(document.querySelectorAll(this.checkoutSelectors.join(',')));
    }

    applyState () {
        const isRestricted = this.restrictedTitles !== '';

        document.documentElement.toggleAttribute('data-wholesale-cart-blocked', isRestricted);

        this.getCheckoutButtons().forEach((button) => {
            if (isRestricted) {
                button.disabled = true;
                button.setAttribute('aria-disabled', 'true');
                button.setAttribute('data-wholesale-blocked', 'true');
            } else if (button.hasAttribute('data-wholesale-blocked')) {
                button.disabled = false;
                button.removeAttribute('aria-disabled');
                button.removeAttribute('data-wholesale-blocked');
            }
        });

        this.syncNotice(isRestricted);
    }

    syncNotice (isRestricted) {
        const existingNotices = document.querySelectorAll('.' + this.noticeClass);
        const firstButton = this.getCheckoutButtons()[0];
        const anchor = firstButton ? firstButton.closest('.cart__ctas') || firstButton.parentElement : null;

        if (!isRestricted || !this.message || !anchor) {
            existingNotices.forEach((notice) => {
                notice.remove();
            });
            return;
        }

        const text = this.message.replace('[products]', this.restrictedTitles);
        const existingNotice = existingNotices[0];

        if (existingNotice && existingNotice.dataset.text === text) {
            return;
        }

        existingNotices.forEach((notice) => {
            notice.remove();
        });

        const notice = document.createElement('p');
        notice.className = this.noticeClass;
        notice.setAttribute('role', 'alert');
        notice.dataset.text = text;
        notice.textContent = text;

        if (this.loginUrl && this.loginLabel) {
            const loginLink = document.createElement('a');
            loginLink.href = this.loginUrl;
            loginLink.textContent = this.loginLabel;
            notice.append(' ', loginLink);
        }

        anchor.parentElement.insertBefore(notice, anchor);
    }

    blockCheckoutClick (event) {
        if (this.restrictedTitles === '' || !event.target.closest) {
            return;
        }

        if (event.target.closest(this.checkoutSelectors.join(','))) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }
}

if (document.currentScript) {
    const wholesaleCartGuard = new WholesaleCartGuard(document.currentScript);
    wholesaleCartGuard.init();
}
